import * as React from 'react';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import styled from 'styled-components';
import { rem } from 'polished';
import { ButtonStyled } from '../../_helpers/form/Button';
import { InputStyled } from '../../_helpers/form/Input';
import OnClickOutsideComponent from '../../_helpers/OnClickOutsideComponent/OnClickOutsideComponent';
import {
  combineWords,
  getBaseUrl,
  isTablet,
  removeDiacriticsToLower,
} from '../../../utilities';
import { Media, MediaContextProvider } from '../../../theme/media';
import OverlaySearch from '../../_helpers/Overlay/OverlaySearch';
import API, { ThenArg } from '../../../services/API';

import loadable from '@loadable/component';

const NEAR_BLACK = '#171717';
const SEARCH_BG = '#f0f0f0';
const SEARCH_BORDER = '#e0e0e0';
const MUTED = '#4d4d4d';
const ACTIVE_BORDER = 'rgba(210, 52, 69, 0.6)';
const ACTIVE_GLOW = 'rgba(210, 52, 69, 0.1)';

const Wrapper = styled.div`
  margin: ${rem(8)} ${rem(12)};
  position: relative;
  z-index: ${(props) => (props.searchDropdownVisible ? 101 : 'inherit')};
  ${({ theme }) => theme.media('md')} {
    margin: ${rem(8)};
  }
`;

const SearchInput = styled(InputStyled)`
  display: block;
  width: 100%;
  height: ${({ $isMobile }) => ($isMobile ? rem(40) : rem(48))};
  background: ${({ $isMobile }) => ($isMobile ? '#ffffff' : SEARCH_BG)};
  border: ${rem(1)} solid ${SEARCH_BORDER};
  border-color: ${({ $isMobile }) =>
    $isMobile ? 'transparent' : SEARCH_BORDER};
  border-radius: ${rem(999)};
  padding: ${rem(12)} ${rem(24)} ${rem(12)} ${rem(52)};
  font-size: ${rem(15)};
  color: ${NEAR_BLACK};
  ${({ theme }) => theme.media('xs')} {
    font-size: ${rem(16)};
  }
  &.has-query {
    padding-right: ${rem(120)};
  }
  &::placeholder {
    color: ${MUTED};
  }
  &:focus {
    outline: none;
    background: #ffffff;
    border-color: ${ACTIVE_BORDER};
    box-shadow: 0 0 0 ${rem(4)} ${ACTIVE_GLOW};
  }
  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
`;

const LeadingIcon = styled.span`
  position: absolute;
  left: ${rem(16)};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  pointer-events: none;
  z-index: 1;
`;

const SearchSubmit = styled(ButtonStyled)`
  position: absolute;
  top: 50%;
  right: ${rem(6)};
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(36)};
  padding: 0 ${rem(24)};
  border: none;
  border-radius: ${rem(999)};
  background: ${({ theme }) => theme.color.secondary};
  color: ${NEAR_BLACK};
  font-size: ${rem(15)};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  &:not(:disabled):hover {
    background: ${({ theme }) => theme.color.secondary};
  }
`;

const SearchGlyph: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
      stroke={NEAR_BLACK}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 22L16 16"
      stroke={NEAR_BLACK}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobileSearchResult = loadable(
  () => import('./SearchResult/MobileSearchResult'),
);

const DesktopSearchResult = loadable(
  () => import('./SearchResult/DesktopSearchResult'),
);

interface SearchformProps {
  searchDropdownVisible: boolean;
  searchedQuery: string;
  searchedResults: any;
  searchedResultsLoader: boolean;
  onChangeSearchInput: (q: string) => void;
  onClickOutsideSearchForm: () => void;
  onRedirect: (url, skvelkoSetId) => void;
  onSubmitSearchInput: (section: string, redirectUrl?: string | null) => void;
  onAppendCategories: () => void;
  onAppendProducts: () => void;
  onAppendWebContents: () => void;
  onSearchClick?: () => void;
  isMobile?: boolean;
  searchVisible?: boolean;
  user: any;
  cart: ThenArg<typeof API.getCart>;
  onSearchFocus?: (isFocus: boolean) => void;
  settings: any;
}

interface SearchformState {
  focused: boolean;
  waitForLoad: boolean;
}

class Searchform extends React.PureComponent<SearchformProps, SearchformState> {
  public searchRef;

  constructor(props) {
    super(props);
    this.state = {
      focused: false,
      waitForLoad: false,
    };

    this.searchRef = React.createRef();
  }

  public handleChangeSearchInput = (e) => {
    this.props.onChangeSearchInput(e.target.value);
  };

  public handleClickOutsideForm = () => {
    if (this.props.searchDropdownVisible) {
      this.props.onClickOutsideSearchForm();
    }
  };

  public onRedirect = (url, skvelkoSetId = null) => {
    this.props.onRedirect(getBaseUrl(url), skvelkoSetId);

    if (this.props.searchDropdownVisible) {
      this.props.onClickOutsideSearchForm();
    }

    if (this.props.onSearchClick) {
      this.props.onSearchClick();
    }
  };

  public onSubmitSearchInput = (
    section: string = '0',
    redirectUrl: string | null = null,
  ) => {
    if (window && window.innerWidth > 991) {
      this.props.onSubmitSearchInput(section, redirectUrl);

      if (this.props.onSearchClick) {
        this.props.onSearchClick();
      }
    }
  };

  public onClearSearchInput = () => {
    this.props.onChangeSearchInput('');
  };

  public setFocus = (isFocus: boolean) => {
    if (this.props.onSearchFocus) {
      this.props.onSearchFocus(isFocus);
    }
    this.setState({
      focused: isFocus,
    });
  };

  public onFocus = () => {
    this.setFocus(true);
  };

  public onBlur = () => {
    this.setFocus(false);
  };

  public onSearchClick = () => {
    if (!this.props.searchedResultsLoader) {
      this.onSubmitSearchInput(
        this.getSearchType(this.props.searchedResults),
        this.getExactLink(this.props.searchedResults),
      );
    } else {
      this.setState({
        waitForLoad: true,
      });
    }
  };

  public onKeyDown = (event) => {
    if (event.key === 'Enter') {
      if (!this.props.searchedResultsLoader) {
        if (this.state.focused) {
          this.onSubmitSearchInput(
            this.getSearchType(this.props.searchedResults),
            this.getExactLink(this.props.searchedResults),
          );
          this.searchRef.current.blur();
        }
      } else {
        this.setState({
          waitForLoad: true,
        });
      }
    }
  };

  public getExactLink(searchedResults): string | null {
    if (this.props.searchedQuery) {
      if (searchedResults?.products?.products?.length) {
        for (const product of searchedResults.products.products) {
          if (
            product.ean === this.props.searchedQuery.trim() ||
            product.plu === this.props.searchedQuery.trim()
          ) {
            return getBaseUrl(product.url);
          }
        }
      }

      if (searchedResults?.products?.demandProducts?.length) {
        for (const product of searchedResults.products.demandProducts) {
          if (
            product.ean === this.props.searchedQuery.trim() ||
            product.plu === this.props.searchedQuery.trim()
          ) {
            return getBaseUrl(product.url);
          }
        }
      }
    }

    return null;
  }

  public getSearchType(searchedResults): string {
    let isInProducts = false;
    let isInDemandProduts = false;
    let isInWeb = false;

    if (
      searchedResults &&
      searchedResults.products &&
      searchedResults.products.products
    ) {
      if (searchedResults?.products?.is_exact_match) {
        isInProducts = true;
      } else {
        for (const product of searchedResults.products.products) {
          if (
            combineWords(
              removeDiacriticsToLower(product.name).split(' '),
            ).includes(removeDiacriticsToLower(this.props.searchedQuery))
          ) {
            isInProducts = true;
            break;
          }
        }
      }
    }

    if (
      searchedResults &&
      searchedResults.demandProducts &&
      searchedResults.demandProducts.products
    ) {
      for (const product of searchedResults.demandProducts.products) {
        if (
          combineWords(
            removeDiacriticsToLower(product.name).split(' '),
          ).includes(removeDiacriticsToLower(this.props.searchedQuery))
        ) {
          isInDemandProduts = true;
          break;
        }
      }
    }

    if (
      searchedResults &&
      searchedResults.webContent &&
      searchedResults.webContent.web_content
    ) {
      for (const webContent of searchedResults.webContent.web_content) {
        if (
          combineWords(
            removeDiacriticsToLower(webContent.name).split(' '),
          ).includes(removeDiacriticsToLower(this.props.searchedQuery))
        ) {
          isInWeb = true;
          break;
        }
      }
    }

    if (!isInDemandProduts && !isInProducts && !isInWeb) {
      const productsLength = searchedResults?.products?.products?.length;
      const demandProductsLength =
        searchedResults?.demandProducts?.products?.length;
      const webContentLength = searchedResults?.webContent?.web_content?.length;

      if (
        productsLength > demandProductsLength &&
        productsLength > webContentLength
      ) {
        isInProducts = true;
      } else if (
        demandProductsLength > productsLength &&
        demandProductsLength > webContentLength
      ) {
        isInDemandProduts = true;
      } else if (
        webContentLength > productsLength &&
        webContentLength > demandProductsLength
      ) {
        isInWeb = true;
      }
    }

    if (isInDemandProduts) {
      return '2';
    } else if (isInProducts) {
      return '0';
    } else if (isInWeb) {
      return '1';
    } else {
      return '0';
    }
  }

  public componentDidMount() {
    if (document) {
      document.addEventListener('keydown', this.onKeyDown, false);
    }
  }
  public componentWillUnmount() {
    if (document) {
      document.removeEventListener('keydown', this.onKeyDown, false);
    }
  }

  public onchange() {
    //
  }

  public onClick = () => {
    this.props.onChangeSearchInput(this.props.searchedQuery);
  };

  public render() {
    const mobileClass = this.props.isMobile ? 'mobile' : '';

    const {
      searchedQuery,
      searchDropdownVisible,
      searchedResults,
      searchedResultsLoader,
      user,
      cart,
      settings,
      onAppendCategories,
      onAppendProducts,
      onAppendWebContents,
    } = this.props;

    if (!this.props.searchedResultsLoader && this.state.waitForLoad) {
      this.setState({
        waitForLoad: false,
      });
      this.onSubmitSearchInput(this.getSearchType(this.props.searchedResults));
    }

    return (
      <>
        {searchDropdownVisible && !isTablet() && <OverlaySearch />}
        <OnClickOutsideComponent onClickOutside={this.handleClickOutsideForm}>
          <Wrapper
            className={mobileClass}
            searchDropdownVisible={searchDropdownVisible}
          >
            <LeadingIcon aria-hidden="true">
              <SearchGlyph />
            </LeadingIcon>
            <SearchInput
              ref={this.searchRef}
              id={'search'}
              type={'search'}
              $isMobile={this.props.isMobile}
              className={searchedQuery !== '' ? 'has-query' : ''}
              value={searchedQuery}
              placeholder={`${__('Hľadajte pero, zošit, stolička')} ...`}
              onInput={this.handleChangeSearchInput}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              onChange={this.onchange}
              onClick={this.onClick}
              autoComplete="off"
              maxLength={150}
            />
            {searchedQuery !== '' && (
              <SearchSubmit
                aria-label={__('Hľadať')}
                onClick={this.onSearchClick}
              >
                {__('Hľadať')}
              </SearchSubmit>
            )}
            <MediaContextProvider>
              <Media lessThan="lg">
                <MobileSearchResult
                  searchDropdownVisible={searchDropdownVisible}
                  searchedQuery={searchedQuery}
                  searchedResults={searchedResults}
                  searchedResultsLoader={searchedResultsLoader}
                  onRedirect={this.onRedirect}
                  onSubmitSearchInput={this.onSubmitSearchInput}
                  onAppendCategories={onAppendCategories}
                  onAppendProducts={onAppendProducts}
                  onAppendWebContents={onAppendWebContents}
                  handleClickOutsideForm={this.handleClickOutsideForm}
                  user={user}
                  cart={cart}
                  settings={settings}
                />
              </Media>
              <Media greaterThanOrEqual="lg">
                <DesktopSearchResult
                  searchDropdownVisible={searchDropdownVisible}
                  searchedQuery={searchedQuery}
                  searchedResults={searchedResults}
                  searchedResultsLoader={searchedResultsLoader}
                  onRedirect={this.onRedirect}
                  onSubmitSearchInput={this.onSubmitSearchInput}
                  onAppendCategories={onAppendCategories}
                  onAppendProducts={onAppendProducts}
                  onAppendWebContents={onAppendWebContents}
                  handleClickOutsideForm={this.handleClickOutsideForm}
                  user={user}
                  cart={cart}
                  settings={settings}
                />
              </Media>
            </MediaContextProvider>
          </Wrapper>
        </OnClickOutsideComponent>
      </>
    );
  }
}

export default Searchform;
